import React from "react"
import { View, StyleSheet } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "../../theme/theme"
import TouchableRippleM from "../button/TouchableRippleM"
import TextM from "../text/TextM"

/**
 * @param {Object} slots
 * NOTE: all values should have the same format as the example below (stringDate à Hour / WeekDay Hour)
 * [
 *   {
 *     "label": "jeudi 18 sept. à 18:00",
 *     "value": "Thursday 18:00"
 *   },
 *   {
 *     "label": "jeudi 18 sept. à 19:00",
 *     "value": "Thursday 19:00"
 *   }
 * ]
 *
 * @param {Function} isSelected - Function to check if a slot is selected, takes the slot value as argument
 * @param {Function} setSelectedSlot - Function to update the selected slot, takes the whole slot object as argument
 *
 * @Example
 * See NewSubscriberPossibleSlotsScreen for multi select example
 * See FastTrackBookTrialSlotStep for single select example
 */
export const SlotPicker = ({ slots, isSelected, setSelectedSlot }) => {
    const formattedSlots = slots.reduce((acc, session) => {
        const [date, time] = session.label.split("à").map((part) => part.trim())

        return {
            ...acc,
            [date]: [...(acc[date] || []), { time, value: session.value }],
        }
    }, {})

    const renderDaySlots = (date, times) => (
        <View
            key={date}
            style={[
                styles.stack,
                styles.card,
                { backgroundColor: theme.colors.white },
            ]}
        >
            <View style={styles.row}>
                <FontAwesome5
                    name="calendar-alt"
                    size={14}
                    color={theme.colors.primary}
                />
                <TextM
                    fontWeight="Bold"
                    capitalize
                    fontSize={16}
                    color={theme.colors.primary}
                >
                    {date}
                </TextM>
            </View>
            <View style={styles.row}>
                {times.map(({ time, value }) => {
                    const isValueSelected = isSelected(value)
                    const iconColor = isValueSelected
                        ? theme.colors.success
                        : theme.colors.primaryLight
                    return (
                        <TouchableRippleM
                            key={value}
                            style={[
                                styles.card,
                                styles.row,
                                styles.clickableCard,
                                isValueSelected && styles.selectedCard,
                            ]}
                            onPress={() =>
                                setSelectedSlot({
                                    value,
                                    label: date + " à " + time,
                                })
                            }
                        >
                            <FontAwesome5
                                name={isValueSelected ? "check" : "clock"}
                                size={14}
                                color={iconColor}
                            />
                            <TextM fontWeight="Bold">{time}</TextM>
                        </TouchableRippleM>
                    )
                })}
            </View>
        </View>
    )

    return (
        <View style={styles.content}>
            {Object.entries(formattedSlots).map(([date, times]) =>
                renderDaySlots(date, times)
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    content: {
        paddingBottom: 40,
        gap: 10,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
    },
    stack: {
        gap: 10,
    },
    card: {
        borderRadius: 12,
        padding: 10,
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
    },
    clickableCard: {
        padding: 16,
        borderColor: theme.colors.lightMore,
    },
    selectedCard: {
        borderColor: theme.colors.success,
        backgroundColor: theme.colors.successLight,
    },
})
